import { TStartAppConfig } from "../../generated/gql/graphql";
import { getAppClientPath } from "../components/pixie/common";
import { DEV_SERVER_URL, PROD_SERVER_URL } from "../constants/common";
import { sendCommandToExtension } from "./interact-with-extension";
import { useClientStore } from "../hooks/ClientState";
import { UpdateChildClientState, isUpdateChildClientState } from "../types/ChromeExtensionTypes";
import { ChildAppClientState } from "../types/ClientState";
import { sendRequestToChromeExtension } from "./chrome-extension";


// NOTE this should be called in parent app code, not content script
export function initChildAppListener(updateClientState: (clientState: Partial<ChildAppClientState>) => void) {
  // TODO add security checks
  function handleUpdateFromChildApp(event: MessageEvent) {
    if (isUpdateChildClientState(event.data)) {
      updateClientState(event.data.clientState);
    }
  }
  window.addEventListener('message', handleUpdateFromChildApp);
  return () => {
    window.removeEventListener('message', handleUpdateFromChildApp);
  }
}

export async function waitForChildAppStart() {
  return await new Promise<string>((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new Error("Timeout waiting for Chrome extension app to connect (10s)"));
    }, 10000);

    const unsub = useClientStore.subscribe(
      (state) => state.lastChildAppClientId,
      (clientId, prevClientId) => {
        if (clientId && clientId !== prevClientId) {
          clearTimeout(timeoutId);
          unsub();
          resolve(clientId);
        }
      }
    );
  });
}


export async function waitForChildAppCompletion(clientId: string) {
  return await new Promise<void>(resolve => {
    const unsub = useClientStore.subscribe(
      (state) => state.childApps[clientId],
      clientState => {
        if (clientState?.completed || clientState?.terminated) {
          unsub();
          resolve();
        }
      }
    );
  });
}


export function notifyParentApp(clientState: Partial<ChildAppClientState> & { clientId: string }, parentTabId?: number) {
  const msg: UpdateChildClientState = {
    action: 'childAppClientStateUpdateRequest',
    clientState,
    parentTabId
  }
  if (parentTabId) {
    // For Chrome extension apps, send through service worker
    sendRequestToChromeExtension(msg);
  } else {
    // For normal web apps, use window.opener
    window.opener?.postMessage(msg);
  }
}

export async function runChildApp(
  runAppConfig: TStartAppConfig,
  parentIsChromeExtension: boolean,
  childIsChromeExtension: boolean,
  useLocalhost?: boolean,
) {
  // sendCommandToExtension work when the parent app is inside chrome extension side panel
  // TODO handle the case when both parent and child are chrome extension app
  if (childIsChromeExtension) {
    sendCommandToExtension({
      type: 'runApp',
      flowId: runAppConfig.appId,
      clientId: runAppConfig.clientId,
      runInBackground: !runAppConfig.waitForCompletion,
    });

  }
  else {
    let appPath = getAppClientPath(runAppConfig.appId);
    // we need to use the full URL to open the app in a new window if parent is inside chrome extension side panel
    if (parentIsChromeExtension) {
      const baseUrl = useLocalhost ? DEV_SERVER_URL : PROD_SERVER_URL;
      appPath = new URL(appPath, baseUrl).toString();
    }
    const openedWindow = window.open(appPath, "popup", "width=800,height=600,location=no,menubar=no,toolbar=no");
    if (!openedWindow) {
      throw new Error("Failed to open application window");
    }
  }
  const clientId = await waitForChildAppStart();
  if (runAppConfig.waitForCompletion) {
    await waitForChildAppCompletion(clientId);
  }
}
